/**
 * Sync Status Indicator Component
 *
 * Header badge showing sync status (green/yellow/red).
 *
 * DESIGN:
 * - Small badge in app header
 * - Minimal intrusion
 * - Clear visual states
 *
 * STATES:
 * - 🟢 Green: "Synced" (genuinely nothing outstanding)
 * - 🟡 Yellow: "Syncing" (outbox has pending items)
 * - 🟡 Yellow: "Not synced yet" (FT-577: something failed, will retry automatically)
 * - 🔴 Red: "Not synced" (FT-577: something PERMANENTLY failed to upload, e.g. a photo)
 * - 🔴 Red: "Offline" (no network connection)
 *
 * FT-577 (2026-11-01): before this, a change that had FAILED (not pending) fell
 * through to green "Synced" — a photo could be permanently lost while the badge
 * said everything was fine. Status now comes from useSyncAttention, which reads
 * failed outbox entries too. Tap/click opens the detail (works on mobile), with
 * a "Try again" button when something failed.
 *
 * `attentionOnly`: render NOTHING unless something needs attention. Used for the
 * mobile header, which had no sync badge at all, so normal mobile UI is
 * unchanged but a failed upload can't go unnoticed.
 *
 * INTEGRATION:
 * - TailwindShell.js header (desktop: always; mobile: attentionOnly)
 */

import React, { useState } from 'react';
import { CloudOff, Loader2, CheckCircle2, AlertTriangle, AlertCircle } from 'lucide-react';
import { useSyncAttention } from '../../hooks/useSyncAttention';

interface SyncStatusIndicatorProps {
  attentionOnly?: boolean;
}

export function SyncStatusIndicator({ attentionOnly = false }: SyncStatusIndicatorProps) {
  const sync = useSyncAttention();
  // Hover (desktop) and tap-to-pin (mobile) are tracked separately: mobile
  // browsers fire an emulated mouseenter AND a click on tap, so a single
  // toggled flag would open and immediately close.
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const showTooltip = hovered || pinned;

  if (attentionOnly && !sync.needsAttention) return null;

  const failedCount = sync.permanentCount + sync.enqueueFailedPhotoCount;

  const getStatusConfig = () => {
    switch (sync.status) {
      case 'failed':
        return {
          icon: AlertTriangle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          label: 'Not synced',
          description: sync.attentionMessage,
        };
      case 'retrying':
        return {
          icon: AlertCircle,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          label: 'Not synced yet',
          description: sync.attentionMessage,
        };
      case 'syncing':
        return {
          icon: Loader2,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          label: 'Syncing',
          description: `${sync.pendingCount} change${sync.pendingCount === 1 ? '' : 's'} pending`,
          animate: true,
        };
      case 'offline':
        return {
          icon: CloudOff,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          label: 'Offline',
          description: 'Changes will sync when online',
        };
      case 'synced':
      default:
        return {
          icon: CheckCircle2,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          label: 'Synced',
          description: 'All changes saved to cloud',
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;
  const animate = 'animate' in config && config.animate;
  // While offline / syncing, still mention any failures in the detail panel.
  const extraAttention = sync.status !== 'failed' && sync.status !== 'retrying' ? sync.attentionMessage : '';

  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPinned(false); }}
      data-testid="sync-status-indicator"
      data-sync-status={sync.status}
      data-sync-ready={sync.loaded ? 'true' : 'false'}
    >
      {/* Status Badge — tap/click pins the detail open (hover-less mobile) */}
      <div
        role="button"
        tabIndex={0}
        aria-label={`Sync status: ${config.label}`}
        aria-expanded={showTooltip}
        onClick={() => setPinned((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPinned((v) => !v); } }}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${config.bgColor} ${config.borderColor} cursor-pointer transition-all hover:shadow-sm`}
      >
        <Icon
          className={`h-4 w-4 ${config.color} ${animate ? 'animate-spin' : ''}`}
        />
        <span className={`text-sm font-medium ${config.color} hidden sm:inline`} data-testid="sync-status-label">
          {config.label}
        </span>
        {failedCount > 0 ? (
          <span className="flex items-center justify-center h-5 min-w-[20px] px-1 bg-red-600 text-white text-xs font-bold rounded-full" data-testid="sync-status-failed-count">
            {failedCount}
          </span>
        ) : sync.pendingCount > 0 && (
          <span className="flex items-center justify-center h-5 min-w-[20px] px-1 bg-yellow-500 text-white text-xs font-bold rounded-full">
            {sync.pendingCount}
          </span>
        )}
      </div>

      {/* Tooltip / detail */}
      {showTooltip && (
        <div className="absolute top-full right-0 mt-2 z-50 w-64 bg-slate-800 text-white rounded-lg shadow-xl p-3" data-testid="sync-status-detail">
          <div className="flex items-center gap-2 mb-2">
            <Icon className={`h-5 w-5 ${animate ? 'animate-spin' : ''}`} />
            <span className="font-semibold">{config.label}</span>
          </div>
          <p className="text-sm text-slate-300" data-testid="sync-status-description">{config.description}</p>
          {extraAttention && (
            <p className="mt-2 text-sm text-red-300" data-testid="sync-status-extra">{extraAttention}</p>
          )}

          {sync.canRetry && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); sync.retryNow(); }}
              disabled={sync.retryInFlight || !sync.isOnline}
              className="mt-3 w-full px-3 py-1.5 rounded-md bg-white text-slate-800 text-sm font-medium hover:bg-slate-100 disabled:opacity-50"
              data-testid="sync-status-retry"
            >
              {sync.retryInFlight ? 'Retrying…' : 'Try again'}
            </button>
          )}
          {sync.retryNote && (
            <p className="mt-2 text-xs text-amber-200" data-testid="sync-status-retry-note">{sync.retryNote}</p>
          )}
          {sync.supportNote && (
            <p className="mt-2 text-xs text-amber-200" data-testid="sync-status-support-note">{sync.supportNote}</p>
          )}

          {/* Additional Details */}
          <div className="mt-3 pt-3 border-t border-slate-700 space-y-1 text-xs text-slate-400">
            <div className="flex justify-between">
              <span>Network:</span>
              <span className={sync.isOnline ? 'text-green-400' : 'text-red-400'}>
                {sync.isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Pending Changes:</span>
              <span className="text-slate-200">{sync.pendingCount}</span>
            </div>
            {failedCount + sync.retryingCount > 0 && (
              <div className="flex justify-between">
                <span>Failed Uploads:</span>
                <span className="text-red-300">{failedCount + sync.retryingCount}</span>
              </div>
            )}
          </div>

          {/* Tooltip Arrow */}
          <div className="absolute -top-1 right-4 w-2 h-2 bg-slate-800 transform rotate-45" />
        </div>
      )}
    </div>
  );
}
